/**
 * Public-domain image picker for the AI create flow, one placeholder at a
 * time: a vertical film strip of answer tiles on the left (search results,
 * a dashed "Generate with AI" placeholder tile, a "Skip" tile — the only
 * scrolling region, and a half-clipped tile is its own scroll cue), with
 * the slide context, a large full-resolution preview of the current choice
 * and the pinned footer filling the rest — nothing else ever scrolls.
 *
 * Accessibility: focus trap, Escape = skip all, focus restore, roving
 * tabindex + arrow-key selection, explicit per-step Next and final confirm.
 */
import { StyledPillButton, VisuallyHidden, colors, focusVisible, text } from "@quickpoint.me/shared";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";
import {
	AiImagePickerAtom,
	AiImagePickerDiscardAtom,
	AiImagePickerGenerateAtom,
	AiImagePickerResolveAtom,
	type GeneratedImage,
	type ImagePickerSelection,
	type PublicDomainImageOption,
} from "../state/aiChatAtoms";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(255, 255, 255, 0.92);
`;

const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #ffffff;
  padding: 2rem;
  border: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
  border-radius: 8px;
  width: 90%;
  max-width: 820px;
  height: min(90vh, 680px);
  /* only the film strip scrolls */
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  font-family: "Avantt-Regular", sans-serif;
  color: #111111;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  z-index: 10001;

  &:focus-visible {
    outline: 3px solid #4a90d9;
    outline-offset: 2px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
`;

/** film strip | main pane */
const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 1.25rem;
`;

/** the one scrolling region: a vertical strip of answer tiles */
const FilmStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  min-height: 0;
  flex-shrink: 0;
  padding: 2px; /* room for the focus ring */

  /* tiles keep their size; the strip scrolls instead of squashing them */
  & > * {
    flex-shrink: 0;
  }
`;

const MainPane = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  margin: 0;
  font-weight: normal;
  color: #111111;
`;

const StepCount = styled.span`
  font-size: 0.9rem;
  color: #333333;
  white-space: nowrap;
`;

/** The slide the image will live in — quiet reading-paper block */
const ContextPanel = styled.blockquote`
  margin: 0;
  padding: 1rem 1.25rem;
  background: #fffff8;
  border: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
  border-radius: 6px;
  font-family: "et-book-roman-line-figures", Palatino, serif;
  font-size: 0.95rem;
  color: #111111;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  .context-title {
    font-size: 1.1rem;
  }
`;

const SlotPrompt = styled.div`
  font-size: 1.05rem;
  color: #111111; /* 18.9:1 (AAA) */
  text-align: center;
`;

/** Large preview of the current choice — fills whatever height is left */
const PreviewPane = styled.div`
  flex: 1;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #f7f7f7;
  border: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
  color: #333333;
`;

const PreviewImgWrap = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
`;

const PromptLabel = styled.label`
  font-size: 0.85rem;
  color: #333333;
`;

const PromptTextarea = styled.textarea`
  width: min(28rem, 100%);
  min-height: 4.5rem;
  padding: 0.6em 0.75em;
  border: 1px solid #757575; /* 3:1 UI minimum */
  border-radius: 6px;
  background: #ffffff;
  color: #111111;
  font-family: inherit;
  font-size: 0.95rem;
  text-align: left;
  resize: vertical;
  ${focusVisible}
`;

const PreviewMeta = styled.div`
  font-size: 0.85rem;
  color: #333333;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35em;
  justify-content: center;
  align-items: baseline;
`;

const SourceLink = styled.a`
  font-size: 0.85rem;
  color: ${colors.teal}; /* link teal, AAA on white */
  text-decoration: underline;
  ${focusVisible}
`;

/** Shared tile chrome: constant 3px border so selection never shifts layout */
const Tile = styled.button.attrs({ type: "button" })<{ $selected: boolean }>`
  position: relative;
  width: 100px;
  height: 100px;
  padding: 0;
  border: 3px solid ${(p) => (p.$selected ? "#5335c9" : "transparent")};
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  background: #f7f7f7;
  font-family: inherit;
  ${focusVisible}

  &:hover {
    border-color: ${(p) => (p.$selected ? "#5335c9" : "#757575")};
  }
`;

/** "Generate with AI" — a dashed placeholder square: the image doesn't exist
    yet. A full-size "AI" over a light sparkle watermark; ink on the lavender
    watermark is ~11:1 and the label is large text besides, so AAA holds
    everywhere. The preview pane spells out what it means. */
const GenerateTile = styled(Tile)`
  background: #ffffff;
  border-style: ${(p) => (p.$selected ? "solid" : "dashed")};
  border-color: ${(p) => (p.$selected ? "#5335c9" : "#757575")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #333333;

  .sparkle-bg {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
    pointer-events: none;
  }

  .ai-label {
    position: relative;
    font-size: 2.4rem;
    /* normal weight: only Avantt-Regular is loaded, so anything heavier
       gets faux-bolded by the browser and looks off-brand */
    font-weight: normal;
  }

  &:hover {
    border-color: #5335c9;
    color: #5335c9;
  }
`;

/** "Skip" — same shape, quieter: an empty frame */
const SkipTile = styled(Tile)`
  background: #ffffff;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  color: #333333;
  font-size: 0.85rem;

  &::before {
    content: "";
    position: absolute;
    inset: 7px;
    border: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
    border-radius: 4px;
    pointer-events: none;
  }

  &:hover {
    color: #5335c9;
  }
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** marks a strip tile as AI-generated */
const SparkleChip = styled.span`
  position: absolute;
  bottom: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
  color: #333333;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const CheckBadge = styled.span`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #5335c9;
  border: 2px solid #ffffff;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  z-index: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding-block-start: 1rem;
  border-block-start: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
`;

const NoResults = styled.span`
  font-size: 0.85rem;
  color: #333333;
  align-self: center;
`;

/** the "keep or discard unused generations?" question */
const DiscardPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
  color: #111111;
`;

const MiniThumbRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const MiniThumb = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.navigationFrame ?? "#E5E1F8"};
`;

const ErrorText = styled.span`
  font-size: 0.85rem;
  color: ${colors.textSafe.red}; /* 7.5:1 (AAA) */
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: #333333;
`;

const SparkleIcon = ({ size = 24 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
		<path
			d="M12 2l2.2 6.3L20.5 10l-6.3 2.2L12 18.5 9.8 12.2 3.5 10l6.3-1.7L12 2z"
			fill="currentColor"
		/>
		<path d="M19 15l1 2.7L22.7 19 20 20l-1 2.7L18 20l-2.7-1 2.7-1.3L19 15z" fill="currentColor" />
	</svg>
);

const SkipIcon = ({ size = 24 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
		<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
		<line x1="6" y1="18" x2="18" y2="6" stroke="currentColor" strokeWidth="2" />
	</svg>
);

type Selection = PublicDomainImageOption | GeneratedImage | "generate" | "skip";

const isGenerated = (s: Selection): s is GeneratedImage =>
	typeof s === "object" && "generatedName" in s;
const isPublicDomain = (s: Selection): s is PublicDomainImageOption =>
	typeof s === "object" && "fullImageUrl" in s;

export function ImagePickerModal() {
	const slots = useRecoilValue(AiImagePickerAtom);
	const resolve = useRecoilValue(AiImagePickerResolveAtom);
	const generateImage = useRecoilValue(AiImagePickerGenerateAtom);
	const discardImages = useRecoilValue(AiImagePickerDiscardAtom);
	const setPicker = useSetRecoilState(AiImagePickerAtom);
	const setResolve = useSetRecoilState(AiImagePickerResolveAtom);

	const [selections, setSelections] = useState<Map<number, Selection>>(new Map());
	const [editedPrompts, setEditedPrompts] = useState<Map<number, string>>(new Map());
	const [generated, setGenerated] = useState<Map<number, GeneratedImage[]>>(new Map());
	const [generating, setGenerating] = useState(false);
	const [generateFailed, setGenerateFailed] = useState(false);
	const [lastAnnouncement, setLastAnnouncement] = useState("");
	const [stepIndex, setStepIndex] = useState(0);
	const [confirmDiscard, setConfirmDiscard] = useState<{
		unused: GeneratedImage[];
		result: ImagePickerSelection[];
	} | null>(null);
	const confirmedRef = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const open = !!slots && slots.length > 0;

	// focus the dialog on open, restore focus on close
	useEffect(() => {
		if (!open) return;
		const previous = document.activeElement as HTMLElement | null;
		containerRef.current?.focus();
		return () => previous?.focus();
	}, [open]);

	// on step change, move focus to the step's tabbable tile
	// biome-ignore lint/correctness/useExhaustiveDependencies: focus follows the step
	useEffect(() => {
		if (!open || stepIndex === 0) return;
		containerRef.current
			?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')
			?.focus();
	}, [stepIndex, open]);

	if (!open) return null;

	const total = slots.length;
	const step = Math.min(stepIndex, total - 1);
	const slot = slots[step];
	const current = selections.get(step);
	const isLast = step === total - 1;
	// the AI tile is a tool, not an answer — generating produces the answers
	const stepAnswered = selections.has(step) && current !== "generate";
	const stepGenerated = generated.get(step) ?? [];

	const finish = (result: ImagePickerSelection[]) => {
		if (!resolve || confirmedRef.current) return;
		confirmedRef.current = true;
		resolve(result);
		setPicker(null);
		setResolve(null);
	};

	const confirmSelected = () => {
		const result: ImagePickerSelection[] = slots.map((_, i) => {
			const choice = selections.get(i) ?? "skip";
			// an un-generated AI tile is not an answer
			if (choice === "generate") return "skip";
			if (isGenerated(choice)) return { generatedName: choice.generatedName };
			return choice;
		});

		// generated more than they used? ask before closing
		const usedNames = new Set(
			result.flatMap((r) =>
				typeof r === "object" && "generatedName" in r ? [r.generatedName] : [],
			),
		);
		const unused = [...generated.values()]
			.flat()
			.filter((g) => !usedNames.has(g.generatedName));

		if (unused.length === 0) {
			finish(result);
		} else {
			setConfirmDiscard({ unused, result });
		}
	};

	const keepUnusedAndFinish = () => {
		if (!confirmDiscard) return;
		finish(confirmDiscard.result);
	};

	const discardUnusedAndFinish = () => {
		if (!confirmDiscard) return;
		// fire-and-forget: deletion shouldn't hold the deck hostage
		discardImages?.(confirmDiscard.unused);
		finish(confirmDiscard.result);
	};

	const skipAll = () => {
		const result: ImagePickerSelection[] = slots.map(() => "skip");
		// bailing out with generations in hand still deserves the question —
		// on this path every generated image is unused
		const unused = [...generated.values()].flat();
		if (unused.length === 0) finish(result);
		else setConfirmDiscard({ unused, result });
	};

	const next = () => {
		if (!stepAnswered) return;
		if (isLast) confirmSelected();
		else setStepIndex(step + 1);
	};

	const back = () => {
		if (step > 0) setStepIndex(step - 1);
	};

	const select = (choice: Selection) => {
		setSelections((prev) => {
			const nextMap = new Map(prev);
			nextMap.set(step, choice);
			return nextMap;
		});
	};

	const handleGenerate = async () => {
		if (!generateImage || generating) return;
		setGenerating(true);
		setGenerateFailed(false);
		const prompt = editedPrompts.get(step)?.trim() || slot.prompt;
		const result = await generateImage(prompt);
		setGenerating(false);
		if (!result) {
			setGenerateFailed(true);
			return;
		}
		// the new candidate joins the strip and becomes the current choice;
		// the AI tile stays for the next refinement round
		setGenerated((prev) => {
			const nextMap = new Map(prev);
			nextMap.set(step, [...(nextMap.get(step) ?? []), result]);
			return nextMap;
		});
		select(result);
		setLastAnnouncement(`${text.imagePicker.aiGenerated}: ${result.prompt}`);
	};

	const options: Selection[] = [...slot.results, ...stepGenerated, "generate", "skip"];

	/** radio pattern: arrows move the selection within the tile group */
	const handleGroupKeys = (e: React.KeyboardEvent) => {
		const forward = e.key === "ArrowRight" || e.key === "ArrowDown";
		const backward = e.key === "ArrowLeft" || e.key === "ArrowUp";
		if (!forward && !backward) return;
		e.preventDefault();

		const currentIndex = current === undefined ? -1 : options.indexOf(current);
		const nextIndex =
			currentIndex === -1
				? 0
				: (currentIndex + (forward ? 1 : -1) + options.length) % options.length;

		select(options[nextIndex]);
		const group = e.currentTarget as HTMLElement;
		const radios = group.querySelectorAll<HTMLElement>('[role="radio"]');
		radios[nextIndex]?.focus();
	};

	const trapFocus = (e: React.KeyboardEvent) => {
		if (e.key === "Escape") {
			e.preventDefault();
			// from the discard question, Escape backs out to the picker
			if (confirmDiscard) setConfirmDiscard(null);
			else skipAll();
			return;
		}
		if (e.key !== "Tab" || !containerRef.current) return;
		const focusables = containerRef.current.querySelectorAll<HTMLElement>(
			'button:not([tabindex="-1"]):not(:disabled), a[href], [tabindex="0"]',
		);
		if (focusables.length === 0) return;
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	// roving tabindex: the chosen tile is tabbable, else the first
	const tabIndexFor = (option: Selection, index: number) =>
		current === option || (current === undefined && index === 0) ? 0 : -1;

	const searching = slot.status === "searching";

	return createPortal(
		<Backdrop>
			<ModalContainer
				ref={containerRef}
				role="dialog"
				aria-modal="true"
				aria-label={`${text.imagePicker.title} — ${text.imagePicker.stepOf(step + 1, total)}`}
				aria-busy={searching}
				tabIndex={-1}
				onKeyDown={trapFocus}
			>
				<TitleRow>
					<Title>{text.imagePicker.title}</Title>
					{total > 1 && (
						<StepCount>{text.imagePicker.stepOf(step + 1, total)}</StepCount>
					)}
				</TitleRow>

				{confirmDiscard && (
					<DiscardPanel>
						<span>
							{text.imagePicker.unusedGenerated(confirmDiscard.unused.length)}
						</span>
						<MiniThumbRow>
							{confirmDiscard.unused.map((g) => (
								<MiniThumb key={g.generatedName} src={g.url} alt={g.prompt} />
							))}
						</MiniThumbRow>
						<div style={{ display: "flex", gap: "0.75rem" }}>
							<StyledPillButton onClick={keepUnusedAndFinish}>
								{text.imagePicker.keepUnused}
							</StyledPillButton>
							<StyledPillButton $variant="danger" onClick={discardUnusedAndFinish}>
								{text.imagePicker.discardUnused}
							</StyledPillButton>
						</div>
					</DiscardPanel>
				)}

				{!confirmDiscard && (
				<>
				<Body>
					{!searching && (
						<FilmStrip
							role="radiogroup"
							aria-labelledby="image-picker-prompt"
							aria-required="true"
							onKeyDown={handleGroupKeys}
						>
							{slot.results.map((result, ri) => {
								const isSelected = current === result;
								return (
									<Tile
										key={ri}
										role="radio"
										aria-checked={isSelected}
										tabIndex={tabIndexFor(result, ri)}
										$selected={isSelected}
										onClick={() => select(result)}
										aria-label={`${result.title} — ${result.source}`}
										title={`${result.title} — ${result.source}`}
									>
										<ThumbImg src={result.thumbnailUrl} alt="" loading="lazy" />
										{isSelected && <CheckBadge aria-hidden="true">✓</CheckBadge>}
									</Tile>
								);
							})}

							{stepGenerated.map((g, gi) => {
								const isSelected = current === g;
								return (
									<Tile
										key={g.generatedName}
										role="radio"
										aria-checked={isSelected}
										tabIndex={tabIndexFor(g, slot.results.length + gi)}
										$selected={isSelected}
										onClick={() => select(g)}
										aria-label={`${text.imagePicker.aiGenerated} — ${g.prompt}`}
										title={`${text.imagePicker.aiGenerated} — ${g.prompt}`}
									>
										<ThumbImg src={g.url} alt="" loading="lazy" />
										<SparkleChip aria-hidden="true">
											<SparkleIcon size={12} />
										</SparkleChip>
										{isSelected && <CheckBadge aria-hidden="true">✓</CheckBadge>}
									</Tile>
								);
							})}

							<GenerateTile
								role="radio"
								aria-checked={current === "generate"}
								tabIndex={tabIndexFor("generate", slot.results.length + stepGenerated.length)}
								$selected={current === "generate"}
								onClick={() => select("generate")}
								aria-label={text.imagePicker.generateWithAi}
								title={text.imagePicker.generateWithAi}
							>
								<span className="sparkle-bg" aria-hidden="true">
									<SparkleIcon size={86} />
								</span>
								<span className="ai-label" aria-hidden="true">AI</span>
								{current === "generate" && <CheckBadge aria-hidden="true">✓</CheckBadge>}
							</GenerateTile>

							<SkipTile
								role="radio"
								aria-checked={current === "skip"}
								tabIndex={tabIndexFor("skip", slot.results.length + stepGenerated.length + 1)}
								$selected={current === "skip"}
								onClick={() => select("skip")}
							>
								<SkipIcon />
								{text.imagePicker.skip}
								{current === "skip" && <CheckBadge aria-hidden="true">✓</CheckBadge>}
							</SkipTile>
						</FilmStrip>
					)}

					<MainPane>
						{slot.context &&
							(slot.context.before.length > 0 || slot.context.after.length > 0) && (
								<ContextPanel>
									{slot.context.before.map((line, i) => (
										<span
											key={`b${i}`}
											className={i === 0 ? "context-title" : undefined}
										>
											{line}
										</span>
									))}
									{slot.context.after.map((line, i) => (
										<span key={`a${i}`}>{line}</span>
									))}
								</ContextPanel>
							)}

						{searching && (
							<SlotPrompt>
								{slot.prompt} — {text.imagePicker.searching}
							</SlotPrompt>
						)}

						{!searching && (
							<>
								<PreviewPane>
									{current === undefined && (
										<span>
											{slot.results.length === 0 && (
												<NoResults>{text.imagePicker.noResults} </NoResults>
											)}
											{text.imagePicker.previewHint}
										</span>
									)}
									{current === "generate" && (
										<>
											<SparkleIcon size={44} />
											<PromptLabel htmlFor="generate-prompt">
												{text.imagePicker.editPromptLabel}
											</PromptLabel>
											<PromptTextarea
												id="generate-prompt"
												value={editedPrompts.get(step) ?? slot.prompt}
												disabled={generating}
												onChange={(e) =>
													setEditedPrompts((prev) =>
														new Map(prev).set(step, e.target.value),
													)
												}
											/>
											<StyledPillButton
												disabled={generating || !generateImage}
												onClick={handleGenerate}
											>
												{generating
													? text.fileUploader.generating
													: text.fileUploader.generate}
											</StyledPillButton>
											<span role="status">
												{generateFailed && (
													<ErrorText>{text.imagePicker.generateFailed}</ErrorText>
												)}
												{!generateFailed && (
													<VisuallyHidden>{lastAnnouncement}</VisuallyHidden>
												)}
											</span>
										</>
									)}
									{current !== undefined && isGenerated(current) && (
										<>
											<PreviewImgWrap>
												<PreviewImg src={current.url} alt={current.prompt} />
											</PreviewImgWrap>
											<PreviewMeta>
												<span>{text.imagePicker.aiGenerated}</span>
												<span>·</span>
												<span>{current.prompt}</span>
											</PreviewMeta>
										</>
									)}
									{current === "skip" && (
										<>
											<SkipIcon size={44} />
											<span>{text.imagePicker.skipPreviewNote}</span>
										</>
									)}
									{current !== undefined && isPublicDomain(current) && (
										<>
											<PreviewImgWrap>
												<PreviewImg src={current.fullImageUrl} alt={current.title} />
											</PreviewImgWrap>
											<PreviewMeta>
												<span>{current.title}</span>
												<SourceLink
													href={current.sourcePageUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													{current.source} · {current.license}
												</SourceLink>
											</PreviewMeta>
										</>
									)}
								</PreviewPane>

								<SlotPrompt id="image-picker-prompt">{slot.prompt}</SlotPrompt>
							</>
						)}
					</MainPane>
				</Body>

				<ButtonRow>
					<StyledPillButton
						$variant="primaryOutline"
						onClick={skipAll}
						style={{ marginInlineEnd: "auto" }}
					>
						{text.imagePicker.skipAll}
					</StyledPillButton>
					{current === "generate" && !stepAnswered && (
						<HelperText>{text.imagePicker.generateToChoose}</HelperText>
					)}
					{total > 1 && (
						<StyledPillButton
							$variant="primaryOutline"
							disabled={step === 0}
							onClick={back}
						>
							{text.imagePicker.back}
						</StyledPillButton>
					)}
					<StyledPillButton disabled={!stepAnswered || searching} onClick={next}>
						{isLast ? text.imagePicker.useSelected : text.imagePicker.next}
					</StyledPillButton>
				</ButtonRow>
				</>
				)}
			</ModalContainer>
		</Backdrop>,
		document.body,
	);
}
